import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon as MoonIcon, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Starfield from "@/components/Starfield";
import { GoogleButton } from "@/pages/Login";

export default function Register() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setSession(data.token, data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#06080d] px-5 py-12 text-white">
      <Starfield count={70} />
      <div className="glass-strong relative z-10 w-full max-w-md overflow-hidden rounded-3xl p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full"
             style={{ background: "radial-gradient(circle, rgba(255,255,255,0.14), transparent 70%)" }} />
        <Link to="/" className="flex items-center gap-2.5" data-testid="register-logo">
          <MoonIcon className="h-6 w-6" strokeWidth={1.5} />
          <span className="font-sans text-lg font-semibold tracking-tight">Quizer</span>
        </Link>
        <h1 className="mt-8 font-sans text-3xl font-light tracking-tight">Create your account.</h1>
        <p className="mt-2 text-sm text-zinc-400">Start turning lessons into mastery.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Full name</label>
            <input
              required value={name} onChange={(e) => setName(e.target.value)}
              data-testid="register-name-input"
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:border-white/30 focus:outline-none"
              placeholder="Ada Lovelace"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email-input"
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:border-white/30 focus:outline-none"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Password</label>
            <div className="relative mt-2">
              <input
                type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 pr-11 text-sm text-white placeholder:text-zinc-500 focus:border-white/30 focus:outline-none"
                placeholder="At least 6 characters"
              />
              <button type="button" onClick={() => setShow((s) => !s)} data-testid="register-password-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}

          <button type="submit" disabled={loading} data-testid="register-submit-button" className="shimmer w-full rounded-xl bg-white py-3 text-sm font-semibold text-[#06080d] transition-opacity disabled:opacity-50">
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <GoogleButton label="Sign up with Google" />

        <p className="mt-6 text-center text-sm text-zinc-400">
          Already have an account?{" "}
          <Link to="/login" data-testid="register-to-login" className="text-white underline-offset-4 hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}
